import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

const THEME_STORAGE_KEY = 'provider.uiTheme';
const LIGHT_THEME = 'light';
const DARK_THEME = 'dark';

function normalizeTheme(value) {
  return value === DARK_THEME ? DARK_THEME : LIGHT_THEME;
}

export function getStoredTheme() {
  try {
    return normalizeTheme(window.localStorage.getItem(THEME_STORAGE_KEY));
  } catch (_) {
    return LIGHT_THEME;
  }
}

export function applyTheme(theme) {
  const nextTheme = normalizeTheme(theme);
  const root = document.documentElement;
  root.dataset.theme = nextTheme;
  root.style.colorScheme = nextTheme;

  document
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute('content', nextTheme === DARK_THEME ? '#071321' : '#f5f5f5');

  return nextTheme;
}

const ThemeContext = createContext({
  theme: LIGHT_THEME,
  isDark: false,
  setTheme: () => {},
  toggleTheme: () => {},
});

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(getStoredTheme);

  const setTheme = useCallback((value) => {
    setThemeState((currentTheme) => {
      const nextTheme = normalizeTheme(
        typeof value === 'function' ? value(currentTheme) : value
      );

      try {
        window.localStorage.setItem(THEME_STORAGE_KEY, nextTheme);
      } catch (_) {
        // Theme still applies for this session when storage is unavailable.
      }

      applyTheme(nextTheme);
      return nextTheme;
    });
  }, []);

  const toggleTheme = useCallback(() => {
    setTheme((currentTheme) =>
      currentTheme === DARK_THEME ? LIGHT_THEME : DARK_THEME
    );
  }, [setTheme]);

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  const value = useMemo(
    () => ({
      theme,
      isDark: theme === DARK_THEME,
      setTheme,
      toggleTheme,
    }),
    [theme, setTheme, toggleTheme]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  return useContext(ThemeContext);
}
